import { useAnalytics } from '@telesafi/analytics-client';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { RootLayout } from '../components/layout';
import { apiBaseUrl, apiSiteSlug } from '../lib/api';
import { emptyLayoutData, layoutQuery } from '../lib/queries';
import {
  DEFAULT_OG,
  EDITORIAL_EMAIL,
  LOGO,
  ORGANIZATION_ID,
  SAME_AS,
  SITE_LOCALE,
  SITE_NAME,
  SITE_NAME_LATIN,
  SITE_URL,
  WEBSITE_ID,
} from '../lib/seo';
import '../styles/app.scss';

const SITE_NAME_AR = SITE_NAME;
const SITE_DESC_AR = 'أخبار المغرب والعالم — تلي صافي';

// Origins the very first paint depends on. Warming the TCP+TLS handshake here
// saves a full round-trip before the hero image or the first API call starts.
const DEFAULT_MEDIA_ORIGIN = 'https://cdn.telesafi.ma';
const MEDIA_ORIGIN = (() => {
  const raw =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (import.meta as any).env?.VITE_S3_PUBLIC_URL ??
    process.env.VITE_S3_PUBLIC_URL ??
    DEFAULT_MEDIA_ORIGIN;
  try {
    return raw ? new URL(raw).origin : null;
  } catch {
    return DEFAULT_MEDIA_ORIGIN;
  }
})();

const ORG_JSONLD = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'NewsMediaOrganization',
      '@id': ORGANIZATION_ID,
      name: SITE_NAME_AR,
      alternateName: SITE_NAME_LATIN,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: LOGO.url,
        width: LOGO.width,
        height: LOGO.height,
      },
      image: LOGO.url,
      inLanguage: 'ar',
      areaServed: 'MA',
      email: EDITORIAL_EMAIL,
      // The accounts publishing under this name. Empty until the real profile
      // URLs land in SOCIAL_LINKS — an unverifiable `sameAs` is a liability,
      // not a signal.
      ...(SAME_AS.length ? { sameAs: [...SAME_AS] } : {}),
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'editorial',
        email: EDITORIAL_EMAIL,
        url: `${SITE_URL}/contact`,
        availableLanguage: ['ar', 'fr'],
      },
    },
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: SITE_URL,
      name: SITE_NAME_AR,
      inLanguage: 'ar',
      publisher: { '@id': ORGANIZATION_ID },
    },
  ],
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(layoutQuery()).catch(() => emptyLayoutData),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: `${SITE_NAME_AR} - Telesafi.ma` },
      { name: 'description', content: SITE_DESC_AR },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      { property: 'og:site_name', content: SITE_NAME_AR },
      { property: 'og:locale', content: SITE_LOCALE },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: SITE_URL },
      { property: 'og:title', content: `${SITE_NAME_AR} - Telesafi.ma` },
      { property: 'og:description', content: SITE_DESC_AR },
      // Site-wide social card fallback. Routes that have their own image
      // override these; without it, shares of any non-article page render blank.
      { property: 'og:image', content: DEFAULT_OG.url },
      // No og:image:width/height here on purpose. Routes override og:image but
      // can only override the dimensions when they actually know them, so a
      // declared 1200x630 survived onto article images of other sizes and told
      // every scraper the wrong shape. The default card's own dimensions are
      // declared by the routes that use it (staticHead, the home page).
      { property: 'og:image:alt', content: SITE_NAME_AR },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: DEFAULT_OG.url },
      { name: 'theme-color', content: '#17222B' },
    ],
    links: [
      { rel: 'shortcut icon', href: '/assets/images/logos/telesafi-favicon.png', type: 'image/png' },
      { rel: 'apple-touch-icon', href: '/assets/images/logos/telesafi-favicon.png' },
      { rel: 'alternate', type: 'application/rss+xml', href: '/rss.xml', title: SITE_NAME_AR },
      ...(MEDIA_ORIGIN
        ? [
            { rel: 'preconnect', href: MEDIA_ORIGIN, crossOrigin: 'anonymous' as const },
            { rel: 'dns-prefetch', href: MEDIA_ORIGIN },
          ]
        : []),
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: ORG_JSONLD,
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

/**
 * Rendered for every 404 (unknown path, dead article slug, out-of-range
 * category page). The response already carries `x-robots-tag: noindex` from
 * the worker; the meta tag repeats it for anything that only reads the
 * document, and overrides the site-wide `index, follow` declared above.
 */
function NotFoundPage() {
  return (
    <section className="about-item-area">
      {/* No <title> here on purpose: React would hoist a second one into a
          head that already has the site title, and duplicate titles are worse
          than a generic one on a page nothing should index anyway. */}
      <meta name="robots" content="noindex, follow" />
      <div className="container">
        <div className="row">
          <div className="col-lg-12 text-center" style={{ padding: '64px 16px' }}>
            <h1 className="title">الصفحة غير موجودة</h1>
            <p className="mt-3">
              الرابط الذي طلبتموه غير متاح، ربما حُذف المقال أو تغيّر عنوانه.
            </p>
            <p className="mt-4">
              <Link to="/">العودة إلى الصفحة الرئيسية</Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useAnalytics({
    endpoint: `${apiBaseUrl}/public/analytics/collect`,
    siteSlug: apiSiteSlug,
    page: { path: pathname },
  });

  const { data } = useQuery({
    ...layoutQuery(),
    placeholderData: emptyLayoutData,
  });
  const layout = data ?? emptyLayoutData;
  return (
    <RootDocument>
      <RootLayout
        categories={layout.categories}
        mainCategories={layout.mainCategories}
        otherCategories={layout.otherCategories}
        trending={layout.trendings}
      >
        <Outlet />
      </RootLayout>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="ar" dir="rtl">
      <head>
        <HeadContent />
      </head>
      <body className="home-1-bg">
        {children}
        <Scripts />
      </body>
    </html>
  );
}
